// src/components/PremiumGuard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

const PremiumGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const auth = getAuth();
  const navigate = useNavigate();
  
  const subscriptionCache = useRef(null);
  const lastCheckTime = useRef(null);
  const CACHE_DURATION = 60 * 1000; // 1 minute cache

  useEffect(() => {
    const checkPremiumStatus = async (user) => {
      if (!user) {
        setLoading(false);
        setIsPremium(false);
        return;
      }

      // Check cache first
      if (subscriptionCache.current && lastCheckTime.current) {
        const timeSinceLastCheck = Date.now() - lastCheckTime.current;
        if (timeSinceLastCheck < CACHE_DURATION) {
          setIsPremium(subscriptionCache.current);
          setLoading(false);
          return;
        }
      }

      try {
        const response = await fetch(
          `https://ftea-backend-321105010733.europe-west9.run.app/api/profile/user_profiles/${user.uid}/subscription-windows`
        );
        const windows = await response.json();
        
        const today = new Date().setHours(0, 0, 0, 0);
        const hasActiveWindow = windows.some(window => {
          const startDate = new Date(window.start_date).setHours(0, 0, 0, 0);
          const endDate = new Date(window.end_date).setHours(0, 0, 0, 0);
          return today >= startDate && today <= endDate;
        });
        
        subscriptionCache.current = hasActiveWindow;
        lastCheckTime.current = Date.now();
        setIsPremium(hasActiveWindow);
      } catch (error) {
        console.error('Error checking premium status:', error);
        subscriptionCache.current = false;
        lastCheckTime.current = Date.now();
        setIsPremium(false);
      }
      setLoading(false);
    };

    const unsubscribe = auth.onAuthStateChanged(user => {
      checkPremiumStatus(user);
    });

    return () => unsubscribe();
  }, [auth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!auth.currentUser) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <h2 className="text-2xl font-bold mb-4">Premium Feature</h2>
        <p className="text-gray-600 mb-4 text-center">
          Please sign in to access premium features.
        </p>
        <button 
          onClick={() => navigate('/auth')}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          Sign In
        </button>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <h2 className="text-2xl font-bold mb-4">Premium Feature</h2>
        <p className="text-gray-600 mb-4 text-center">
          This feature requires an active subscription. Create an account to acquire access to Premium Features.
        </p>
        <button 
          onClick={() => navigate('/app/dashboard')}
          className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          Get Access
        </button>
      </div>
    );
  }

  return children;
};

export default PremiumGuard;